/**
 * Dashboard Component
 * 
 * Homepage for all user types (guests, employees, employers).
 * Contains hero section with quick search and detailed search filters.
 * 
 * @route /
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { X, MapPin, Briefcase, Search } from 'lucide-react';
import { saveSearchHistory, fetchSearchHistory, getLocalSearchHistory, deleteSearchHistory, SearchHistoryItem } from '../services/searchHistoryService';
import './Dashboard.scss';

interface SearchChip {
  id: string;
  label: string;
  type: 'title' | 'location' | 'work_field' | 'salary' | 'keyword';
}

interface AutocompleteSuggestion {
  label: string;
  type: 'title' | 'location' | 'work_field' | 'salary';
  count: number | null;
}

const CHIP_BADGE: Record<string, string> = {
  location: 'Miestas',
  work_field: 'Sritis',
  title: 'Pareigybė',
  salary: 'Atlyginimas',
};

const Dashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [keywordInput, setKeywordInput] = useState('');
  const [chips, setChips] = useState<SearchChip[]>([]);
  const [recentSearches, setRecentSearches] = useState<SearchHistoryItem[]>([]);
  const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>(undefined);
  const chipIdRef = useRef(0);

  const isEmployerMode = searchParams.get('mode') === 'employer' || user?.user_type === 'employer';

  const nextChipId = () => String(++chipIdRef.current);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Salary: digits only → synthetic suggestion, no API call
    if (/^\d+$/.test(query)) {
      setSuggestions([{ label: `Nuo ${query}€`, type: 'salary', count: null }]);
      setShowSuggestions(true);
      setActiveSuggestion(-1);
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_URL}/jobs/search/autocomplete?q=${encodeURIComponent(query)}&limit=7`);
      if (res.ok) {
        const data = await res.json();
        const filtered = (data.suggestions || []).filter(
          (s: AutocompleteSuggestion) => s.count === null || s.count > 0
        );
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
        setActiveSuggestion(-1);
      }
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleInputChange = (value: string) => {
    setKeywordInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 200);
  };

  const selectSuggestion = (suggestion: AutocompleteSuggestion) => {
    const chipType = suggestion.type;
    const label = suggestion.label;
    // Prevent duplicate chips with same label+type
    if (!chips.some(c => c.label === label && c.type === chipType)) {
      setChips(prev => [...prev, { id: nextChipId(), label, type: chipType }]);
    }
    setKeywordInput('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const maxSuggestions = 7;
  const visibleSuggestions = suggestions.slice(0, maxSuggestions);

  const handleKeywordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && visibleSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveSuggestion(prev => prev < visibleSuggestions.length - 1 ? prev + 1 : 0);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveSuggestion(prev => prev > 0 ? prev - 1 : visibleSuggestions.length - 1);
        return;
      }
      if (e.key === 'Enter' && activeSuggestion >= 0) {
        e.preventDefault();
        selectSuggestion(visibleSuggestions[activeSuggestion]);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (keywordInput.trim()) {
        const text = keywordInput.trim();
        if (!chips.some(c => c.label === text && c.type === 'keyword')) {
          setChips(prev => [...prev, { id: nextChipId(), label: text, type: 'keyword' }]);
        }
        setKeywordInput('');
        setShowSuggestions(false);
      }
    }
  };

  const removeChip = (chipId: string) => {
    setChips(prev => prev.filter(c => c.id !== chipId));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const locationChips = chips.filter(c => c.type === 'location').map(c => c.label);
    const workFieldChips = chips.filter(c => c.type === 'work_field').map(c => c.label);
    const salaryChip = chips.find(c => c.type === 'salary');
    const keywordChips = chips.filter(c => c.type === 'keyword' || c.type === 'title').map(c => c.label);

    if (keywordInput.trim()) keywordChips.push(keywordInput.trim());

    const params = new URLSearchParams();
    if (keywordChips.length) params.set('keywords', keywordChips.join(','));
    if (locationChips.length) params.set('location', locationChips.join(','));
    if (workFieldChips.length) params.set('workField', workFieldChips.join(','));
    if (salaryChip) {
      const amount = salaryChip.label.replace(/\D/g, '');
      if (amount) params.set('salaryMin', amount);
    }

    setKeywordInput('');

    const allLabels = [...keywordChips, ...locationChips, ...workFieldChips].filter(Boolean);
    const title = allLabels.join(', ') || (salaryChip ? salaryChip.label : '');
    const paramsObj: Record<string, string> = {};
    params.forEach((v, k) => { paramsObj[k] = v; });
    if (title) saveSearchHistory(title, paramsObj, isAuthenticated);

    const queryString = params.toString();
    if (isEmployerMode) {
      navigate(`/skelbimai${queryString ? `?${queryString}` : ''}`);
    } else {
      navigate(`/darbo-skelbimai${queryString ? `?${queryString}` : ''}`);
    }
  };

  useEffect(() => {
    const loadHistory = async () => {
      if (isAuthenticated) {
        const items = await fetchSearchHistory(5);
        setRecentSearches(items);
      } else {
        setRecentSearches(getLocalSearchHistory(5));
      }
    };
    loadHistory();
  }, [isAuthenticated]);

  const handleRecentSearchClick = (search: SearchHistoryItem) => {
    const queryString = new URLSearchParams(search.searchParams).toString();
    if (isEmployerMode) {
      navigate(`/skelbimai${queryString ? `?${queryString}` : ''}`);
    } else {
      navigate(`/darbo-skelbimai${queryString ? `?${queryString}` : ''}`);
    }
  };

  const handleDeleteRecentSearch = async (e: React.MouseEvent, searchId: number | string) => {
    e.stopPropagation();
    await deleteSearchHistory(searchId, isAuthenticated);
    setRecentSearches(prev => prev.filter(s => s.id !== searchId));
  };

  // Handle URL-based auth modal opening for marketing campaigns
  React.useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'register') {
      // Could trigger registration modal here if needed
      setSearchParams({}); // Clear the URL parameter
    }
  }, [searchParams, setSearchParams]);

  return (
    <section className="hero-section">
      <div className="hero-background"></div>
      <div className="container">
        <div className="hero-content">
          <h1>Gauk tik tau skirtus skelbimus</h1>
          <p className="hero-subtitle">Greitai. Paprastai. Efektyviai</p>

          {/* SEARCH FORM */}
          <div className="search-form-container" ref={suggestionsRef}>
            <form className="main-search-form" onSubmit={handleSearch}>
              <div className="search-input-wrapper">
                <div className="chips-and-input">
                  {chips.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className="keyword-chip-inline"
                      onClick={() => removeChip(chip.id)}
                    >
                      {CHIP_BADGE[chip.type] && <span className="chip-badge">{CHIP_BADGE[chip.type]}</span>}
                      <span>{chip.label}</span>
                      <span className="remove-chip">×</span>
                    </button>
                  ))}
                  
                  <input
                    type="text"
                    className="search-input-inline"
                    placeholder={chips.length === 0 ? "Įrašyk kokio darbo ieškai" : ""}
                    value={keywordInput}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={handleKeywordKeyDown}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    autoComplete="off"
                  />
                </div>
                
                <button type="submit" className="search-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  IEŠKOTI
                </button>
              </div>
            </form>

            {showSuggestions && visibleSuggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {visibleSuggestions.map((s, i) => (
                  <div
                    key={`${s.type}-${s.label}`}
                    className={`suggestion-item ${i === activeSuggestion ? 'active' : ''}`}
                    onMouseDown={() => selectSuggestion(s)}
                    onMouseEnter={() => setActiveSuggestion(i)}
                  >
                    <span className="suggestion-icon">
                      {s.type === 'location' && <MapPin size={14} />}
                      {s.type === 'work_field' && <Briefcase size={14} />}
                      {s.type === 'title' && <Search size={14} />}
                      {s.type === 'salary' && <span style={{ fontSize: 14, fontWeight: 600 }}>€</span>}
                    </span>
                    <span className="suggestion-text">{s.label}</span>
                    <span className="suggestion-meta">
                      <span className="suggestion-type">{CHIP_BADGE[s.type] || ''}</span>
                      {s.count != null && <span className="suggestion-count">({s.count.toLocaleString('lt-LT')})</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Searches */}
          {recentSearches.length > 0 && (
            <div className="recent-searches-section">
              <div className="recent-searches-header">
                <span className="section-title">PASKUTINĖS PAIEŠKOS</span>
              </div>
              <div className="recent-searches-list">
                {recentSearches.map((search) => (
                  <div 
                    key={search.id} 
                    className="recent-search-item"
                    onClick={() => handleRecentSearchClick(search)}
                  >
                    <span className="search-title">{search.title}</span>
                    <button 
                      className="delete-search-btn"
                      onClick={(e) => handleDeleteRecentSearch(e, search.id)}
                      title="Ištrinti"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};

export default Dashboard;
